import React, { useCallback } from 'react';
import styles from '../AdminUserCfpSessions.module.css';
import {
  CalendarIcon,
  DocumentIcon,
  EmptyIcon,
  TimestampCell,
} from './helpers';

const TABLE_COLUMNS = [
  { key: 'name', label: 'Name', className: 'col-name' },
  { key: 'expert', label: 'Expert', className: 'col-expert' },
  { key: 'topic', label: 'Topic', className: 'col-topic' },
  { key: 'status', label: 'Status', className: 'col-status' },
  { key: 'created', label: 'Created', className: 'col-created' },
  { key: 'sessionDate', label: 'Session Date', className: 'col-session-date' },
  { key: 'document', label: 'Document', className: 'col-document', headerClass: 'compact-cell' },
];

const SessionRow = React.memo(function SessionRow({
  item,
  index,
  showActionColumn,
  onOpenDocuments,
  onOpenDoneConfirm,
}) {
  const { row, userName, topicName, expertName, statusMeta, canMarkDone } = item;

  return (
    <tr className={styles['session-row']} style={{ '--row-index': index }}>
      <td className={styles['name-cell']} data-label="Name">
        <span className={styles['name-text']}>{userName}</span>
      </td>
      <td className={styles['chip-cell']} data-label="Expert">
        <span className={`${styles.chip} ${styles['chip-expert']}`}>{expertName}</span>
      </td>
      <td className={styles['chip-cell']} data-label="Topic">
        <span className={`${styles.chip} ${styles['chip-topic']}`}>{topicName}</span>
      </td>
      <td className={styles['status-cell']} data-label="Status">
        <span className={`${styles['status-tag']} ${styles[`status-tag-${statusMeta.tone}`]}`}>
          {statusMeta.label}
        </span>
      </td>
      <td className={styles['date-cell']} data-label="Created">
        <TimestampCell value={row?.created_at} />
      </td>
      <td className={styles['date-cell']} data-label="Session Date">
        <TimestampCell value={row?.schedule_at} />
      </td>
      <td className={styles['compact-cell']} data-label="Document">
        <div className={styles['cell-inner']}>
          <button
            type="button"
            className={styles['document-button']}
            onClick={() => onOpenDocuments(row, topicName)}
            title="View session documents"
            aria-label="View session documents"
          >
            <DocumentIcon />
          </button>
        </div>
      </td>
      {showActionColumn && (
        <td className={`${styles['action-cell']} ${styles['compact-cell']}`} data-label="Action">
          <div className={styles['cell-inner']}>
            {canMarkDone ? (
              <button
                type="button"
                className={styles['session-done-button']}
                onClick={() => onOpenDoneConfirm(row)}
                title="Mark session as done"
                aria-label={`Mark ${userName}'s session as done`}
              >
                Mark session as done
              </button>
            ) : (
              <span className={styles['empty-value']}>—</span>
            )}
          </div>
        </td>
      )}
    </tr>
  );
});

const SessionsTable = React.memo(function SessionsTable({
  rows,
  statusFilters = [],
  statusFilter,
  onStatusFilterChange,
  showActionColumn,
  onOpenDocuments,
  onOpenDoneConfirm,
}) {
  const handleFilterChange = useCallback((event) => {
    onStatusFilterChange(event.target.value);
  }, [onStatusFilterChange]);

  return (
    <div className={styles['table-container']}>
      <div className={styles['table-header-section']}>
        <h2 className={styles['table-title']}>
          <CalendarIcon />
          CFP Sessions
          <span className={styles['count-badge']}>
            {rows.length} {rows.length === 1 ? 'session' : 'sessions'}
          </span>
        </h2>
        <div className={styles['table-toolbar']}>
          <label className={styles['filter-label']} htmlFor="admin-cfp-status-filter">
            Filter
          </label>
          <select
            id="admin-cfp-status-filter"
            className={styles['status-select']}
            value={statusFilter}
            onChange={handleFilterChange}
            aria-label="Filter by status"
          >
            {statusFilters.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className={styles['empty-state']} role="status">
          <EmptyIcon />
          <p className={styles['empty-title']}>No sessions found</p>
          <p className={styles['empty-subtitle']}>
            {statusFilter === 'paid'
              ? 'No paid or initial CFP sessions yet.'
              : 'No completed CFP sessions yet.'}
          </p>
        </div>
      ) : (
        <div className={styles['table-wrapper']}>
          <table
            className={`${styles['sessions-table']} ${
              showActionColumn ? styles['has-action'] : styles['no-action']
            }`}
          >
            <colgroup>
              {TABLE_COLUMNS.map((column) => (
                <col key={column.key} className={styles[column.className]} />
              ))}
              {showActionColumn && <col className={styles['col-action']} />}
            </colgroup>
            <thead>
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={column.headerClass ? styles[column.headerClass] : undefined}
                  >
                    {column.label}
                  </th>
                ))}
                {showActionColumn && (
                  <th scope="col" className={styles['compact-cell']}>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((item, index) => (
                <SessionRow
                  key={item.key}
                  item={item}
                  index={index}
                  showActionColumn={showActionColumn}
                  onOpenDocuments={onOpenDocuments}
                  onOpenDoneConfirm={onOpenDoneConfirm}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

export default SessionsTable;
